import { useEffect, useMemo, useRef, useState } from 'react';
import { childNames, propsChild } from '../../redux/child/types';
import cls from './ChildComponent.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import gsap from 'gsap';
import {
  setFall,
  setIsDied,
  setPlay,
  setReachedTargets,
  setStart,
  setStop,
} from '../../redux/child/slice';
import { RootState } from '../../redux/store';
import { decreaseCharge } from '../../redux/game/slice';
import kidFallMp3 from '../../assets/sound/kid_fall_scream.mp3';
import boostKickMp3 from '../../assets/sound/boost_kick.mp3';
import kidBeepMp3 from '../../assets/sound/kid_beep.mp3';
import boostCarabinMp3 from '../../assets/sound/boost_carabin_1.mp3';
import { Howl } from 'howler';
import { updateLastClickTime } from '../../redux/hooks/slice';

const soundFall = new Howl({
  src: [kidFallMp3],
});

const soundBoostKick = new Howl({
  src: [boostKickMp3],
});

const soundKidStop = new Howl({
  src: [kidBeepMp3],
});

const soundAnchor = new Howl({
  src: [boostCarabinMp3],
});

const ChildComponent = ({
  child,
  name,
  leftStatic,
  leftUp,
  static_img,
  anim_down_img,
  anim_up_img,
}: {
  child: propsChild;
  name: childNames;
  leftStatic: number;
  leftUp: number;
  static_img: string;
  anim_up_img: string;
  anim_down_img: string;
}) => {
  const dispatch = useDispatch();
  const { charge } = useSelector((state: RootState) => state.game);
  const { anchor, leg, anchorPosition } = useSelector((state: RootState) => state.hooks);
  const isSound = useSelector((state: RootState) => state.sound.isSound);
  const timelines = useRef<{ [key: string]: gsap.core.Timeline }>({});
  const timeouts = useRef<{ [key: string]: NodeJS.Timeout | null }>({});
  const [isChildAnchor, setIsChildAnchor] = useState(false);

  const updateTimeline = (name: childNames, type: string) => {
    const yPos = Number(gsap.getProperty(`.${name}`, 'y'));

    if (type === 'up') {
      const distanceToMoveUp = -(window.innerHeight - 300 + 10) - yPos;

      const multiplyWhenLeg = leg ? 1.2 : 1;
      console.log(multiplyWhenLeg);
      const newDuration = Math.max(
        (Math.abs(distanceToMoveUp) / (window.innerHeight - 300 + 10)) * 10,
        1, // минимальное значение
      );

      //@ts-ignore
      if (timelines.current[name + '_up']) {
        timelines.current[name + '_up'].kill();
      }
      timelines.current[name + '_up'] = gsap
        .timeline({ paused: true })
        .to(`.${name}`, {
          y: `-${window.innerHeight - 300}`,
          duration: `${newDuration / multiplyWhenLeg}`,
          ease: 'none',
        })
        .eventCallback('onUpdate', () => {
          const yPos = Number(gsap.getProperty(`.${name}`, 'y'));
          if (yPos <= -(window.innerHeight - 300 - 10)) {
            gsap.to(`.${name}`, {
              opacity: 0,
              duration: 1,
              onComplete: () => {
                //@ts-ignore
                if (timelines.current[name + '_up']) {
                  timelines.current[name + '_up'].pause();
                }
                dispatch(setReachedTargets(name));
              },
            });
          }
        });

      // Остановка в рандомный момент
      clearTimeout(timeouts.current[`${name}_stop`] as NodeJS.Timeout); // Очищаем предыдущий таймер

      timeouts.current[`${name}_stop`] = setTimeout(() => {
        if (!leg) {
          dispatch(setStop(name));
        }
      }, Math.random() * (7000 - 2000) + 2000);

      // Запуск анимации, если допустимо
      if (!child.stop && !child.fall) {
        timelines.current[name + '_up'].invalidate().restart();
      }
      // DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN
      // DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN
      // DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN DOWN
    } else if (type === 'down') {
      const distanceToMoveDown = 100 - yPos;
      const newDuration = Math.max(
        (Math.abs(distanceToMoveDown) / (window.innerHeight - 300)) * 10,
        0.1,
      );
      //@ts-ignore
      if (timelines.current[name + '_down']) {
        timelines.current[name + '_down'].kill();
      }
      timelines.current[name + '_down'] = gsap
        .timeline({ paused: true })
        .to(`.${name}`, {
          y: distanceToMoveDown,
          duration: newDuration,
          ease: 'none',
        })
        .eventCallback('onUpdate', () => {
          const yPos = Number(gsap.getProperty(`.${name}`, 'y'));
          if (yPos >= 100) {
            gsap.to(`.${name}`, {
              opacity: 0,
              duration: 0.5,
              onComplete: () => {
                //@ts-ignore
                if (timelines.current[name + '_down']) {
                  timelines.current[name + '_down'].pause();
                }
                dispatch(setIsDied(name));
              },
            });
          }
        });

      if (child.fall) {
        timelines.current[name + '_down'].restart();
      }
    }
  };

  useEffect(() => {
    if (child.start) {
      if (child.fall) {
        if (isSound) {
          soundFall.play();
        }
        setIsChildAnchor(false);
        updateTimeline(name, 'down');
        //@ts-ignore
        if (timelines.current[name + '_up'] && timelines.current[name + '_down']) {
          timelines.current[name + '_up'].pause(); // Останавливаем анимацию восхождения
          timelines.current[name + '_down'].play();
        }
      } else if (child.stop) {
        if (isSound) {
          soundKidStop.play();
        }
        //@ts-ignore
        if (timelines.current[name + '_up']) {
          timelines.current[name + '_up'].pause(); // Останавливаем анимацию восхождения
        }
        timeouts.current[`${name}_fall`] = setTimeout(
          () => {
            dispatch(setFall(name));
            //@ts-ignore
            if (timelines.current[name + '_up']) {
              timelines.current[name + '_up'].kill();
            }
          },
          isChildAnchor ? 5000 : 2000,
        );
      } else {
        setIsChildAnchor(false);
        updateTimeline(name, 'up');
        clearTimeout(timeouts.current[`${name}_fall`] as NodeJS.Timeout); // Очищаем предыдущий таймер
        //@ts-ignore
        if (timelines.current[name + '_up'] && timelines.current[name + '_down']) {
          timelines.current[name + '_down'].pause(); // Останавливаем анимацию падения
          timelines.current[name + '_up'].play();
        }
      }
    }
  }, [child]);

  useEffect(() => {
    if (leg) {
      dispatch(setPlay(name));
      if (isSound) {
        soundBoostKick.play();
      }
    }
  }, [leg]);

  const handleClick = (name: childNames) => {
    if (charge !== 0) {
      dispatch(setPlay(name));
      dispatch(decreaseCharge());
      dispatch(updateLastClickTime());
    }
  };

  function doRectanglesOverlap(A: any, B: any) {
    // Проверка по горизонтали
    if (A.left >= B.right - 30 || B.left + 30 >= A.right) {
      return false;
    }

    // Проверка по вертикали
    if (A.top < B.bottom || B.top < A.bottom) {
      return false;
    }

    return true;
  }

  useEffect(() => {
    const child = document.querySelector(`.${name}`);

    const isCollide = doRectanglesOverlap(
      {
        left: anchorPosition.left,
        top: anchorPosition.bottom,
        right: anchorPosition.right,
        bottom: anchorPosition.top,
      },
      {
        left: child?.getBoundingClientRect().left,
        top: child?.getBoundingClientRect().bottom,
        right: child?.getBoundingClientRect().right,
        bottom: child?.getBoundingClientRect().top,
      },
    );

    if (isCollide) {
      if (isSound) {
        soundAnchor.play();
      }
      // //@ts-ignore
      // if (timelines.current[name + '_down']) {
      //   timelines.current[name + '_down'].pause();
      // }
      dispatch(setPlay(name));
      // dispatch(setStop(name));

      setTimeout(() => {
        dispatch(setStop(name));
        setIsChildAnchor(true);
      }, 10);
      // setTimeout(() => {
      //   dispatch(setPlay(name));
      //   dispatch(setStop(name));
      //   dispatch(setFall(name));
      // }, 5000);
    }
  }, [anchorPosition]);

  useEffect(() => {
    return () => {
      for (const key in timelines.current) {
        //@ts-ignore
        if (timelines.current[key]) {
          timelines.current[key].kill();
        }
      }

      // Очищаем все таймеры
      for (const key in timeouts.current) {
        if (timeouts.current[key]) {
          //@ts-ignore
          clearTimeout(timeouts.current[key]);
        }
      }
    };
  }, []);

  useEffect(() => {
    dispatch(setPlay(name)); //38430q874089798370
    const interval = setTimeout(() => {
      dispatch(setStart(name));
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  const getImg = () => {
    if (isChildAnchor) {
      return child.anchorImg;
    }
    if (child.stop || child.fall) {
      return anim_down_img;
    } else {
      return anim_up_img;
    }
  };

  return (
    <>
      {!child.start ? (
        <img
          key={`static_${name}`}
          alt="Child"
          src={static_img}
          className={`${name} static`}
          style={{ left: `${leftStatic}px` }}
          onClick={() => handleClick(name)}
        />
      ) : (
        <img
          key={`animated_${name}`}
          alt="Child"
          src={getImg()}
          className={`${name} ${cls.child}`}
          style={{ left: `${leftUp}px`, height: `125px` }}
          onClick={() => handleClick(name)}
          onMouseDown={(ev) => ev.preventDefault()}
        />
      )}
    </>
  );
};

export default ChildComponent;
